import { slot } from "./dom.js";
import type { TieredElements } from "./tool-state.js";

/**
 * Finding the parts of the page the hanzi tool drives.
 *
 * Its own module because the shape of the template is a separate concern from
 * what the tool does with it, and because the answer is all-or-nothing: a
 * template missing a slot yields nothing at all, so the tool declines to start
 * rather than half-working in a way nobody would notice.
 */

/**
 * Everything on the page this tool drives.
 *
 * Gathered once at start-up, so that a template that has lost a slot fails
 * where it can be seen rather than silently doing half its job later.
 */
export interface ToolElements extends TieredElements {
  readonly output: HTMLElement;
  readonly copy: HTMLButtonElement | undefined;
  readonly transcriptions: readonly HTMLElement[];
}

/**
 * Find the tool's parts, or nothing where the template is missing one.
 */
export function findElements(container: HTMLElement): ToolElements | undefined {
  const input = slot(container, "input");
  const output = slot(container, "output");
  const status = slot(container, "status");
  const controls = slot(container, "controls");

  if (
    !(input instanceof HTMLTextAreaElement) ||
    output === undefined ||
    status === undefined ||
    controls === undefined
  ) {
    return undefined;
  }

  const upgrade = slot(container, "upgrade");
  const upgradePanel = slot(container, "upgrade-panel");
  const revert = slot(container, "revert");
  const revertPanel = slot(container, "revert-panel");
  const copy = slot(container, "copy");

  return {
    input,
    output,
    status,
    controls,
    upgrade: upgrade instanceof HTMLButtonElement ? upgrade : undefined,
    upgradePanel,
    revert: revert instanceof HTMLButtonElement ? revert : undefined,
    revertPanel,
    copy: copy instanceof HTMLButtonElement ? copy : undefined,
    transcriptions: [
      ...container.querySelectorAll<HTMLElement>("[data-cb-transcription]"),
    ],
  };
}
